import { useParams } from '@tanstack/react-router';
import { AGGREGATE_NAMES, EUROPACE_PROCESS_LINK_CATEGORIES } from 'cpool-constants';
import type { SchemaEuropaceProcessOfferEvent } from 'cpool-schemas';
import { useTranslation } from 'react-i18next';
import { LuExternalLink } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { useOpenEuropacePortalLink } from '~/features/leadDetails/components/LeadSideInfo/ExternalServices/hooks/useOpenEuropacePortalLink.ts';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';

type Props = {
    link: NonNullable<SchemaEuropaceProcessOfferEvent['links']>[number];
    europaceOfferId?: SchemaEuropaceProcessOfferEvent['europaceOfferId'];
};

export const Link: React.FC<Props> = ({ europaceOfferId, link }) => {
    const { t } = useTranslation();
    const loanRequestId = useParams({ strict: false })?.leadId;
    const { permissions } = useAclPermissions({ loanRequestId });
    const { isPending: isPortalLinkPending, mutate: openPortalLink } = useOpenEuropacePortalLink({
        europaceOfferId,
        loanRequestId,
    });
    if (!link.title && !link.url) return null;

    const isPortalLink = link.category === EUROPACE_PROCESS_LINK_CATEGORIES.EXTERNE_FALLBEARBEITUNG;
    const isPortalLinkExpired = isPortalLink && !!link.expiresAt && new Date(link.expiresAt).getTime() <= Date.now();
    const isPortalLinkPermitted =
        !!europaceOfferId &&
        !!loanRequestId &&
        permissions.resolvers[AGGREGATE_NAMES.LOAN_REQUEST].getEuropacePortalLink;

    const openPortal = () => {
        const portalWindow = window.open('', '_blank');
        if (portalWindow) {
            portalWindow.opener = null;
            portalWindow.document.title = t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.OPENING_PORTAL');
            portalWindow.document.body.textContent = t(
                'SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.OPENING_PORTAL',
            );
        }
        openPortalLink({ portalWindow });
    };

    const openLink = async () => {
        if (!link.url) {
            toast.error(t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.TOAST_ERROR.LINK_NOT_PROVIDED'));
            return;
        }
        // bank portal links need the bank token, decrypted server side for the assigned adviser only
        if (isPortalLink) {
            openPortal();
            return;
        }
        try {
            const response = await fetch(link.url, { method: 'POST' });

            if (response.ok) {
                const responseURL = response.url;
                window.open(responseURL, '_blank');
            } else {
                const errorText = await response.text();
                throw new Error(errorText);
            }
        } catch {
            toast.error(t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.TOAST_ERROR.OPEN_LINK_FAILED'));
        }
    };

    const label = link.title || t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.DEFAULT_DOCUMENT_NAME');

    if (!link.url)
        return (
            <li className="flex gap-1.5">
                <span className="shrink-0 select-none">–</span>
                <span>{label}</span>
            </li>
        );
    const isDisabled = isPortalLink && (isPortalLinkExpired || !isPortalLinkPermitted || isPortalLinkPending);
    return (
        <li className="flex gap-1.5">
            <span className="shrink-0 select-none">–</span>
            <button
                type="button"
                onClick={() => openLink()}
                disabled={isDisabled}
                title={
                    isPortalLink && !isPortalLinkExpired && !isPortalLinkPermitted
                        ? t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.PORTAL_LINK_NOT_PERMITTED')
                        : undefined
                }
                className="text-left text-blue disabled:cursor-not-allowed disabled:text-grey"
            >
                <span className="underline">{label}</span>{' '}
                {isPortalLinkExpired ? (
                    <span>({t('SIDE_INFO.EXTERNAL_SERVICES.EUROPACE.PROCESS_EVENTS.PORTAL_LINK_EXPIRED')})</span>
                ) : (
                    <LuExternalLink className="inline h-[15px] w-[15px] align-text-bottom" />
                )}
            </button>
        </li>
    );
};
